import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import PageEmpty from "@/components/feature/PageEmpty";
import {
  formatViews,
  getCategoryName,
  getNewsById,
  getRelatedNews,
  getTopicName,
} from "@/utils/news";
import { useFavorites } from "@/hooks/useFavorites";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function DetailPage() {
  const { newsId = "" } = useParams();
  const item = getNewsById(newsId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useDocumentTitle(
    item ? `${item.title} - 911爆料` : "新闻不存在 - 911爆料",
    item?.summary
  );

  useEffect(() => {
    if (!toast) {
      return;
    }
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!item) {
    return (
      <PageEmpty
        icon="ri-file-unknow-line"
        title="该新闻已被删除或不存在"
        description="您访问的爆料内容可能已被下线，请返回首页浏览最新报道。"
        actionLabel="返回首页"
        actionTo="/"
      />
    );
  }

  const related = getRelatedNews(item, 3);
  const favorite = isFavorite(item.id);

  const handleFavorite = () => {
    toggleFavorite(item.id);
    setToast(favorite ? "已取消收藏" : "收藏成功");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="mx-auto w-full max-w-shell animate-fade-up px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: `${getCategoryName(item.category)}新闻`, to: `/category/${item.category}` },
          { label: item.title },
        ]}
      />

      <article className="mt-6">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/category/${item.category}`}
            className="rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-800 transition-colors hover:bg-primary-200"
          >
            {getCategoryName(item.category)}
          </Link>
          {item.exclusive && (
            <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-background-50">
              独家
            </span>
          )}
        </div>

        <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
          {item.title}
        </h1>

        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-xs text-foreground-500 md:text-sm">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-3-line" />
            {item.author}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-flag-line" />
            来源：{item.source}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line" />
            {item.publishedAt}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-eye-line" />
            {formatViews(item.views)} 次阅读
          </span>

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-500 hover:text-foreground-950"
            >
              <i className="ri-link" />
              复制链接
            </button>
            <button
              type="button"
              onClick={handleFavorite}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-1.5 text-xs transition-colors ${
                favorite
                  ? "border-accent-500 bg-accent-500 font-semibold text-background-50"
                  : "border-background-300 text-foreground-700 hover:border-accent-500 hover:text-accent-400"
              }`}
            >
              <i className={favorite ? "ri-bookmark-fill" : "ri-bookmark-line"} />
              {favorite ? "已收藏" : "收藏"}
            </button>
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-lg border border-background-200">
          <img
            src={item.image}
            alt={item.title}
            title={`${item.title} - 911爆料`}
            className="h-64 w-full object-cover object-top md:h-[420px]"
          />
        </div>

        <p className="mt-6 border-l-2 border-primary-500 pl-4 text-sm font-medium leading-relaxed text-foreground-700 md:text-base">
          {item.summary}
        </p>

        <div className="mt-8 space-y-5">
          {item.content.map((block, index) => {
            if (block.type === "heading") {
              const id = `section-${index}`;
              return (
                <h2
                  key={index}
                  id={id}
                  className="scroll-mt-24 pt-2 font-heading text-lg font-bold text-foreground-950 md:text-xl"
                >
                  <a href={`#${id}`} className="flex items-center gap-2">
                    <span className="h-4 w-1 rounded-full bg-primary-500" />
                    {block.text}
                  </a>
                </h2>
              );
            }
            if (block.type === "image") {
              return (
                <figure key={index} className="overflow-hidden rounded-lg border border-background-200">
                  <img
                    src={block.url}
                    alt={block.caption ?? item.title}
                    title={`${item.title} - 911爆料`}
                    className="h-56 w-full object-cover object-top md:h-80"
                  />
                  {block.caption && (
                    <figcaption className="bg-background-100 px-4 py-2.5 text-xs text-foreground-500">
                      {block.caption}
                    </figcaption>
                  )}
                </figure>
              );
            }
            return (
              <p
                key={index}
                className="text-sm leading-8 text-foreground-700 md:text-base md:leading-9"
              >
                {block.text}
              </p>
            );
          })}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
          <span className="text-xs text-foreground-500">相关入口：</span>
          <Link
            to={`/category/${item.category}`}
            className="flex items-center gap-1.5 rounded-md bg-background-200 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800"
          >
            <i className="ri-folder-line" />
            {getCategoryName(item.category)}分类
          </Link>
          {item.topics.map((topicId) => (
            <Link
              key={topicId}
              to={`/topic/${topicId}`}
              className="flex items-center gap-1.5 rounded-md bg-background-200 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-accent-100 hover:text-accent-800"
            >
              <i className="ri-stack-line" />
              {getTopicName(topicId)}专题
            </Link>
          ))}
        </div>
      </article>

      <section className="mt-14">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
          <span className="h-5 w-1.5 rounded-full bg-primary-500" />
          相关爆料推荐
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((relatedItem) => (
            <NewsCard key={relatedItem.id} item={relatedItem} layout="vertical" />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 animate-soft-in">
          <div className="flex items-center gap-2 rounded-md bg-background-200 px-4 py-2.5 text-sm text-foreground-950 shadow-none">
            <i className="ri-checkbox-circle-fill text-accent-400" />
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}